import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useRebrand } from '@growtherapy/sprout-ui';

import { ObscuredContentProps } from '../../a11y';
import { Navbar, PageContainer, PageProviders, PushDrawerContainer, StageContainer } from '../../components';
import { logger } from '../../datadog';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { TelehealthSessionInfoData_TelehealthSessionInfo_Appointment, useGetTelehealthSessionInfo } from '../../hooks';
import { useInactiveTabPerformanceOptimization } from '../../hooks/useInactiveTabPerformanceOptimization';
import { useOnInactiveMobileScreenDetected } from '../../hooks/useOnInactiveMobileScreenDetected';
import { useWaitingRoomTrackingEvent } from '../../hooks/useWaitingRoomTrackingEvent';
import { EventTypeEnum } from '../../segment/types';
import { useHandleDeviceChanges, useInitLocalAudioVideoContext } from '../../session';
import { Theme } from '../../state';
import { HelpModalWrapper as HelpModal } from '../HelpModalWrapper';
import { PushDrawerWrapper as PushDrawer } from '../PushDrawerWrapper';
import { SelfVideoThumbnail } from '../SelfVideoThumbnail';
import { useObscuredVisitorContentProps } from '../a11y';
import { WAITING_ROOM_PUSH_DRAWER_ARIA_CONTROLS_ID } from '../constants';
import {
  VisitorDrawerState,
  VisitorState,
  preSessionVibesOpenAtom,
  visitorDrawerStateAtom,
  visitorIsHelpModalOpenAtom,
  visitorNameAtom,
} from '../state';
import { useInitTwilioSyncClient } from '../sync/useInitTwilioSyncClient';
import { ProviderInfoSectionWrapper as ProviderInfoSection } from './ProviderInfoSectionWrapper';
import { ProviderNotHereModalWrapper as ProviderNotHereModal } from './ProviderNotHereModalWrapper';
import { RoomLoaderWrapper as RoomLoader } from './RoomLoaderWrapper';
import { SessionInfoSectionWrapper as SessionInfoSection } from './SessionInfoSectionWrapper';
import { AdmissionModalWrapper as AdmissionModal } from './admission/AdmissionModalWrapper';
import { useAnimationState } from './background-action';
import { BackgroundAnimationContainerWrapper as BackgroundAnimationContainer } from './background-action/BackgroundAnimationContainerWrapper';
import { useInitVisitorChatAccess, useWaitingRoomConversation } from './chat';
import { VisitorWaitingSessionControlsWrapper as SessionControls } from './controls/VisitorWaitingSessionControlsWrapper';
import { PreSessionPromptVariant } from './pre-session-prompt/PreSessionPrompt';
import { usePreSessionPromptState } from './pre-session-prompt/usePreSessionPromptState';
import { PreSessionVibesWrapper as PreSessionVibes } from './pre-session-vibes/PreSessionVibesWrapper';
import { useAutoOpenPreSessionDrawer } from './useAutoOpenPreSessionDrawer';
import { useClearProviderNoShowTabTitleCallback } from './useClearProviderNoShowTabTitle';
import { useResetUserStateCallback } from './useResetUserStateCallback';
import { useUpdateTitleOnProviderPastNoShowThreshold } from './useUpdateTitleOnProviderPastNoShowThreshold';
import { useUpdateVisitorPresenceInterval } from './useUpdateVisitorPresenceInterval';
import { useWaitingRoomTimeout } from './useWaitingRoomTimeout';
import { useWatchForRoomToken } from './useWatchForRoomToken';

function Stage({
  obscuredContentProps,
  pauseAnimation,
  appointment,
}: {
  obscuredContentProps: Partial<ObscuredContentProps>;
  pauseAnimation: () => void;
  appointment?: TelehealthSessionInfoData_TelehealthSessionInfo_Appointment | null;
}) {
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);

  const preSessionVibesOpen = useAtomValue(preSessionVibesOpenAtom);
  const { preSessionPrompt } = usePreSessionPromptState();
  const preSessionPromptIncludesVibes =
    preSessionPrompt === PreSessionPromptVariant.VIBES ||
    preSessionPrompt === PreSessionPromptVariant.REFLECTION_TOPICS;
  const showPreSessionVibes = preSessionPromptIncludesVibes && preSessionVibesOpen;

  const openDrawer = useCallback(
    (drawer: VisitorDrawerState): void => {
      setDrawerState(drawer);
      // Pause the background animation when the drawer is opened.
      pauseAnimation();
    },
    [setDrawerState, pauseAnimation],
  );

  return (
    <StageContainer {...obscuredContentProps} className='gap-y-0 @container'>
      <BackgroundAnimationContainer isPreSessionVibesOpen={showPreSessionVibes}>
        <ProviderInfoSection />
        <div className='flex flex-col flex-grow gap-y-8 justify-center items-center'>
          {showPreSessionVibes ? (
            <PreSessionVibes />
          ) : (
            <SessionInfoSection appointment={appointment} openDrawer={openDrawer} />
          )}
          <MobileSelfVideoThumbnail />
        </div>
      </BackgroundAnimationContainer>
      <DesktopSelfVideoThumbnail />
    </StageContainer>
  );
}

function PageEffects() {
  const resetUserState = useResetUserStateCallback();
  const visitorName = useAtomValue(visitorNameAtom);
  useInitLocalAudioVideoContext();
  useInitTwilioSyncClient();
  useWatchForRoomToken();
  useUpdateVisitorPresenceInterval();
  useHandleDeviceChanges();
  useWaitingRoomTimeout();

  useInitVisitorChatAccess();
  useWaitingRoomConversation();
  useOnInactiveMobileScreenDetected(() => {
    resetUserState({ state: VisitorState.TAB_AWAY, name: visitorName });
    logger.info('Exited waiting room due to inactive screen');
  });
  useUpdateTitleOnProviderPastNoShowThreshold();
  useInactiveTabPerformanceOptimization();
  const { providerShortId, patientShortId } = useParams();

  useWaitingRoomTrackingEvent(EventTypeEnum.CLIENT_WAITING_ROOM, providerShortId, patientShortId);

  return null;
}

export function MobileSelfVideoThumbnail() {
  return (
    <SelfVideoThumbnail
      showExpandVideoToggle={false}
      shouldShowHideSelf={false}
      showSetPositionMenu={false}
      className='sm:hidden relative self-center mt-2 mb-6 sm:mt-10 sm:mb-10'
      videoClassName='min-h-[12rem] sm:min-h-[14rem] max-h-[18rem] min-w-[calc(100vw-2rem)] sm:min-w-[25rem] max-w-screen-sm'
    />
  );
}

export function DesktopSelfVideoThumbnail() {
  return (
    <SelfVideoThumbnail
      showExpandVideoToggle
      shouldShowHideSelf
      showSetPositionMenu
      className='hidden sm:inline absolute'
    />
  );
}

export function Page() {
  const isRebrand = useRebrand();
  const obscuredContentProps = useObscuredVisitorContentProps();

  const [isHelpModalOpen, setIsHelpModalOpen] = useAtom(visitorIsHelpModalOpenAtom);

  const clearProviderNoShowTabTitle = useClearProviderNoShowTabTitleCallback();
  const { providerShortId, patientShortId } = useParams();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);

  const appointment = data?.telehealthSessionInfo?.appointment;

  const { pause: pauseAnimation, play: playAnimation } = useAnimationState();

  // Pause the background animation if the measures drawer is auto-opened.
  useAutoOpenPreSessionDrawer({ onAutoOpen: pauseAnimation });

  const openHelpModal = useCallback(() => {
    sendLoggingEvents(TrackingEvents.ANY_HELP_REQUEST);
    sendLoggingEvents(TrackingEvents.CLIENT_HELP_REQUEST);
    setIsHelpModalOpen(true);
    // Pause the background animation when the help modal is opened.
    pauseAnimation();
  }, [setIsHelpModalOpen, pauseAnimation]);

  return (
    <>
      <PageEffects />
      <PageProviders theme={isRebrand ? Theme.DARK : Theme.LIGHT}>
        <AdmissionModal />
        <HelpModal
          isOpen={isHelpModalOpen}
          onClose={() => {
            setIsHelpModalOpen(false);
            clearProviderNoShowTabTitle();
            playAnimation();
          }}
          shouldShowProviderNoShowSection
          appointment={appointment}
        />
        <ProviderNotHereModal onOpenModal={pauseAnimation} onCloseModal={playAnimation} />
        <PageContainer>
          <Navbar
            {...obscuredContentProps}
            className='order-1'
            onClickNeedHelpButton={openHelpModal}
            showLanguageSwitcher
          />
          <SessionControls />
          <PushDrawerContainer
            // The push drawer must come _after_ the buttons that can trigger it
            // in the DOM. This is so that a keyboard user can tab forward from
            // the triggering button to the drawer. However, the drawer and
            // stage are meant to be visually positioned _before_ the control
            // buttons, so we reorder them within their flex container.
            className='order-2 sm:px-2'
          >
            <Stage
              obscuredContentProps={obscuredContentProps}
              pauseAnimation={pauseAnimation}
              appointment={appointment}
            />
            <PushDrawer
              className='sm:my-2 sm:h-auto'
              id={WAITING_ROOM_PUSH_DRAWER_ARIA_CONTROLS_ID}
              onCloseDrawer={playAnimation}
            />
          </PushDrawerContainer>
          <RoomLoader />
        </PageContainer>
      </PageProviders>
    </>
  );
}
